// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { OverflowMenuPresentationProps } from "@rpsc/MobiusOverrides/OverflowMenu";
import { PanelMenuPresentationProps } from "@rpsc/MobiusOverrides/PanelMenu";
import IconArrowLeftLink from "@rpsc/XDL/Icons/IconArrowLeftLink";
import IconArrowRightLink from "@rpsc/XDL/Icons/IconArrowRightLink";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { ellipsis, styledScrollbar, xScreen } from "@rpsc/XDL/Utilities";
import color, { getSystemColor } from "@rpsc/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@rpsc/XDL/Utilities/links";
import { mergeWithCss } from "@rpsc/XDL/Utilities/mergeWithCss";
import { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui4",
        },
        text: {
            default: "text",
            hover: "text",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 5px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 13px;
                padding: 10px 15px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: TypographyPresentationProps,
    iconStyles?: IconPresentationProps,
    iconPosition: "left" | "right" = "left",
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                mergeCss: true,
                typographyProps: mergeWithCss(
                    {
                        size: 12,
                        lineHeight: "17px",
                        weight: fontConfig.weight.bold,
                        css: css`
                            letter-spacing: 0;
                        `,
                    },
                    typographyStyles,
                ),
                icon: {
                    iconProps: mergeWithCss(
                        {
                            src: icon,
                            size: 14,
                            css: css`
                                ${marginPosition}: 8px;
                                opacity: 1;
                            `,
                        },
                        iconStyles,
                    ),
                    position: linkStyles?.icon?.position || iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        line-height: 12px;
                    `,
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                    display: flex;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "tertiary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            min-height: 31px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const sharedProductDescriptionStyles = (lines = 2, sm?: number): ProductDescriptionStyles => {
    let smLines = sm;
    if (!smLines) {
        smLines = lines;
    }
    return {
        productDetailDescription: {
            size: 14,
            color: "text",
            weight: "bold",
            css: css`
                display: block;
                letter-spacing: 0;
                line-height: 16px;
                margin-bottom: 8px;
                ${ellipsis(lines)}
                ${xScreen("lte", "sm")} {
                    ${ellipsis(smLines)}
                }
                @media print {
                    display: block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
        productDetailLink: {
            color: "text",
            typographyProps: {
                size: 14,
                color: "text",
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 16px;
                    ${ellipsis(lines)}
                    ${xScreen("lte", "sm")} {
                        ${ellipsis(smLines)}
                    }
                `,
            },
            css: css`
                margin-bottom: 8px;
                align-items: flex-start;
                @media print {
                    display: inline-block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
    };
};

export const dotButtonStyles: ButtonPresentationProps = {
    css: css`
        height: 10px;
        width: 10px;
        min-height: unset;
        padding: 0;
        margin-right: 4px;
        border: 1px solid ${color("common.ui1")};
        border-radius: 5px;
        background: transparent;

        &:active,
        &:focus {
            &&&& {
                width: 20px;
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }
        &:hover {
            &&&& {
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }

        &.slide-dot-button-selected {
            width: 20px;
            background: ${color("common.ui1")};
        }
    `,
};

export const selectedPanelItem = css`
    background: ${getSystemColor("common.ui9")};
`;
export const sharedPanelMenuStyles: PanelMenuPresentationProps = {
    mergeCss: true,
    backIconProps: {
        src: ChevronLeft,
        color: "text.link",
        css: css`
            opacity: 0.66;
            margin-right: 0;
            &,
            & svg {
                width: 13px;
                height: 13px;
            }
        `,
    },
    panelChildrenSeparator: {
        css: css`
            margin: 16px auto 10px;
            border-top: 1px solid ${color("common.border")};
            width: calc(100% - 32px);
        `,
    },
    cssOverrides: {
        children: css`
            background-color: ${color("common.white")};
            max-height: calc(100vh - 300px);
            & > button {
                padding: 0px;
                font-size: 14px;
                line-height: 22px;
                font-weight: ${fontConfig.weight.regular};
                min-height: 22px;
                color: ${color("common.black")};
                ${IconWrapper} {
                    color: ${color("text.link")};
                }
            }
            .panel-menu {
                & > ${StyledButton} {
                    margin: 0;
                }
            }
        `,
        wrapper: css`
            & > ${IconWrapper} {
                margin-right: 8px;
                color: ${color("text.link")};
            }
            margin: 0 8px;
            border-radius: 2px;
            &:hover:not(:disabled),
            &:hover:not(:disabled) > ${StyledButton}, &:focus:not(:disabled),
            &:focus:not(:disabled) > ${StyledButton} {
                // if it has selected item then consider it as selected
                ${selectedPanelItem}
            }
        `,
        menu: css`
            display: flex;
            flex-direction: column;
            row-gap: 4px;
            ${styledScrollbar()}
            overflow-x: hidden;
        `,
    },
    panelRowProps: {
        css: css`
            padding: 4px 8px;
            border-radius: 2px;
            text-align: left;
            margin: 0 8px;
            ${IconWrapper} {
                color: ${color("text.link")};
                width: auto;
                height: 100%;
            }

            ${TypographyStyle} {
                font-size: 14px;
                line-height: 22px;
                font-weight: ${fontConfig.weight.regular};
                color: ${color("text")};
            }
            &:hover,
            &:focus {
                ${selectedPanelItem}
            }
        `,
        isCurrentCss: css`
            ${selectedPanelItem}
        `,
    },
    backLink: {
        icon: {
            iconProps: {
                src: IconArrowLeftLink,
                size: 13,
            },
        },
        typographyProps: {
            weight: fontConfig.weight.bold,
            css: css`
                font-size: 14px;
                line-height: "17px";
            `,
        },
    },
    parentLinkButton: {
        icon: {
            position: "right",
            iconProps: {
                src: IconArrowRightLink,
                size: 12,
            },
        },
        typographyProps: {
            size: 14,
            lineHeight: "22px",
            weight: fontConfig.weight.bold,
        },
        css: css`
            width: fit-content;
            padding: 0 16px;
            margin-bottom: 8px;
        `,
    },
    parentLinkWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            padding-left: 16px;
            padding-top: 6px;
            ${StyledButton} {
                ${TypographyStyle} {
                    font-size: 12px;
                }
            }
        `,
    },
} as any;

export const emptyValueStyles = {
    css: css`
        font-size: 30px;
        color: ${color("common.ui5")};
    `,
};

export const StyledHr = getStyledWrapper("hr");

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}
export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                display: flex;
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
                @media print {
                    display: none;
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,
            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 17px;
                color: ${color("primary")};
                font-weight: ${fontConfig.weight.bold};
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: calc(100% - 12px);
                    margin-top: 8px;
                }
                &:hover {
                    color: ${color("common.ui8")};
                    background: none;
                }
            `,
        },
    },
    moreActionsLink: {
        icon: {
            iconProps: {
                src: ChevronDown,
                size: 14,
            },
            position: "right",
        },
    },
};

export const sharedExpandButtonIconsStyles: LinkPresentationProps = {
    icon: {
        iconProps: {
            css: css`
                &,
                svg {
                    width: 11px;
                    height: 13px;
                }
            `,
        },
    },
};
